import { useEffect, useMemo, useState } from 'react'
import { buildPaginationTokens } from '../../../../utils/paginationTokens'
import { extractApiErrorMessage } from '../../../../utils/apiError'
import { useMultiSelect } from '../../../../hooks/useMultiSelect'
import { useApprovalSearchParams } from '../../hooks/useApprovalSearchParams'
import { buildApprovalUserOptions } from '../../utils/approvalFilterOptions'
import {
  approveLeave,
  fetchLeaveApprovalQueue,
  fetchLeaveApprovalUsers,
  rejectLeave,
  bulkApproveLeaves,
  bulkRejectLeaves,
} from '../api/leaveApprovalApi'

const DEFAULT_PAGE_SIZE = 10

function formatDate(value) {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

function leaveTypeLabel(value) {
  const normalized = String(value || '').toUpperCase()
  if (normalized === 'HALF_DAY') return 'Half Day'
  return 'Full Day'
}

export function useLeaveApprovalScreen() {
  const { searchParams, updateParam, setPageParam } = useApprovalSearchParams()
  const page = Number(searchParams.get('page') || 1)
  const searchText = searchParams.get('search') || ''
  const statusFilter = searchParams.get('status') || 'PENDING'
  const userFilter = searchParams.get('user_id') || 'all'
  const fromDate = searchParams.get('fromDate') || ''
  const toDate = searchParams.get('toDate') || ''

  const [pageSize] = useState(DEFAULT_PAGE_SIZE)
  const [userOptions, setUserOptions] = useState([])
  const [rows, setRows] = useState([])
  const [meta, setMeta] = useState({
    total: 0,
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    hasMore: false,
  })
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [totals, setTotals] = useState({ pending: 0, approved: 0, rejected: 0 })
  const [actionError, setActionError] = useState('')
  const [actionMessage, setActionMessage] = useState('')
  const [activeActionId, setActiveActionId] = useState(null)
  const [refreshTick, setRefreshTick] = useState(0)
  
  const { isMultiSelectMode, selectedIds, toggleMultiSelectMode, toggleSelection, selectAll, clearSelection } = useMultiSelect()

  const queryParams = useMemo(() => {
    const params = {
      page,
      page_size: pageSize,
      approval_status: statusFilter === 'all' ? 'ALL' : statusFilter,
    }
    if (searchText.trim()) params.search = searchText.trim()
    if (fromDate) params.date_from = fromDate
    if (toDate) params.date_to = toDate
    if (userFilter !== 'all') params.user_id = Number(userFilter)
    return params
  }, [page, pageSize, statusFilter, userFilter, fromDate, toDate, searchText])

  useEffect(() => {
    let active = true

    async function loadRows() {
      setIsLoading(true)
      setError('')
      try {
        const response = await fetchLeaveApprovalQueue(queryParams)
        if (!active) return
        const items = Array.isArray(response?.items) ? response.items : []
        setRows(items)
        setMeta({
          total: Number(response?.total || 0),
          page: Number(response?.page || page),
          pageSize: Number(response?.pageSize || pageSize),
          hasMore: Boolean(response?.hasMore),
        })
        setTotals(response?.approvalSummary || { pending: 0, approved: 0, rejected: 0 })
      } catch (requestError) {
        if (!active) return
        setRows([])
        setError(extractApiErrorMessage(requestError, 'Failed to load leave approval queue.'))
      } finally {
        if (active) setIsLoading(false)
      }
    }

    loadRows()
    return () => {
      active = false
    }
  }, [page, pageSize, queryParams, refreshTick])

  useEffect(() => {
    let active = true

    async function loadUserOptions() {
      try {
        const options = await fetchLeaveApprovalUsers({
          approval_status: statusFilter === 'all' ? 'ALL' : statusFilter,
          date_from: fromDate || undefined,
          date_to: toDate || undefined,
        })
        if (!active) return
        setUserOptions(Array.isArray(options) ? options : [])
      } catch {
        if (!active) return
        // Keep prior options; URL state must not be cleared by an option error.
      }
    }

    loadUserOptions()
    return () => {
      active = false
    }
  }, [statusFilter, fromDate, toDate])

  const mappedRows = useMemo(() => {
    return rows.map((row) => ({
      id: Number(row.id),
      userName: row.userName || '-',
      leaveDate: formatDate(row.leaveDate),
      leaveType: leaveTypeLabel(row.leaveType),
      remark: row.remark || '-',
      approvalStatus: String(row.approvalStatus || 'PENDING').toUpperCase(),
      decisionRemark: row.decisionRemark || '-',
      createdAt: formatDate(row.createdAt),
      canApprove: Boolean(row.canApprove),
    }))
  }, [rows])

  const totalPages = useMemo(() => {
    if (!meta.total || !meta.pageSize) return meta.hasMore ? page + 1 : Math.max(1, page)
    return Math.max(1, Math.ceil(meta.total / meta.pageSize))
  }, [meta.hasMore, meta.pageSize, meta.total, page])

  const rangeStart = useMemo(() => {
    if (!mappedRows.length) return 0
    return (page - 1) * pageSize + 1
  }, [mappedRows.length, page, pageSize])

  const rangeEnd = useMemo(() => {
    if (!mappedRows.length) return 0
    return rangeStart + mappedRows.length - 1
  }, [mappedRows.length, rangeStart])



  function goToPage(nextPage) {
    if (nextPage < 1 || nextPage > totalPages) return
    setPageParam(nextPage)
  }

  function goToPreviousPage() {
    goToPage(Math.max(1, page - 1))
  }

  function goToNextPage() {
    if (meta.hasMore || page < totalPages) goToPage(page + 1)
  }

  async function runAction(leaveId, actionType, decisionRemark = '') {
    setActionError('')
    setActionMessage('')
    setActiveActionId(leaveId)
    try {
      if (actionType === 'approve') {
        await approveLeave(leaveId)
        setActionMessage('Leave approved successfully.')
      } else {
        await rejectLeave(leaveId, { decisionRemark })
        setActionMessage('Leave rejected successfully.')
      }
      setRefreshTick((prev) => prev + 1)
    } catch (requestError) {
      setActionError(extractApiErrorMessage(requestError, 'Action failed.'))
    } finally {
      setActiveActionId(null)
    }
  }


  async function runBulkApprovalAction(actionType) {
    if (selectedIds.size === 0) return
    const ids = Array.from(selectedIds)
    setActionError('')
    setActionMessage('')
    try {
      if (actionType === 'approve') {
        await bulkApproveLeaves({ ids })
        setActionMessage(`Successfully approved ${ids.length} leave(s).`)
      } else {
        await bulkRejectLeaves({ ids })
        setActionMessage(`Successfully rejected ${ids.length} leave(s).`)
      }
      clearSelection()
      setRefreshTick((prev) => prev + 1)
    } catch (requestError) {
      setActionError(extractApiErrorMessage(requestError, 'Bulk action failed.'))
    }
  }

  return {
    state: {
      isLoading,
      error,
      actionError,
      actionMessage,
      activeActionId,
      rows: mappedRows,
      page,
      totalPages,
      pageTokens: buildPaginationTokens(page, totalPages),
      totalCount: meta.total,
      hasMore: meta.hasMore,
      rangeStart,
      rangeEnd,
      searchText,
      statusFilter,
      userFilter,
      userOptions: buildApprovalUserOptions(userOptions, userFilter),
      fromDate,
      toDate,
      totals,
      isMultiSelectMode,
      selectedIds,
    },
    actions: {
      setSearchText: (value) => updateParam('search', value),
      setStatusFilter: (value) => updateParam('status', value),
      setUserFilter: (value) => updateParam('user_id', value),
      setFromDate: (value) => updateParam('fromDate', value),
      setToDate: (value) => updateParam('toDate', value),
      goToPreviousPage,
      goToNextPage,
      goToPage,
      approveLeave: (leaveId) => runAction(leaveId, 'approve'),
      rejectLeave: (leaveId, decisionRemark = '') => runAction(leaveId, 'reject', decisionRemark),
      toggleMultiSelectMode,
      toggleSelection,
      selectAll: () => {
        const selectable = mappedRows.filter(r => r.approvalStatus === 'PENDING' && r.canApprove).map(r => r.id)
        selectAll(selectable)
      },
      bulkApprove: () => runBulkApprovalAction('approve'),
      bulkReject: () => runBulkApprovalAction('reject'),
    },
  }
}
